class QuickAddModal {
  constructor() {
    this.modal = document.getElementById("quick-add-modal");
    this.modalContent = document.getElementById("quick-add-modal-content");
    this.closeButton = document.querySelector(".quick-add-modal__close");
    this.currentProductHandle = null;
    this.currentProductData = null;
    this.currentZoomIndex = 0; // Track current zoomed image index

    if (!this.modal || !this.modalContent || !this.closeButton) return;
    this.initEvents();
  }

  initEvents() {
    this.closeButton.addEventListener("click", () => this.close());
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });

    document.querySelectorAll(".quick-add-btn").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const productHandle = button.dataset.productHandle;
        if (productHandle) this.open(productHandle);
      });
    });
  }

  open(productHandle, templateView) {
    if (!this.modal || !this.modalContent) return;
    if (
      this.currentProductHandle === productHandle &&
      this.modal.style.display === "flex"
    ) {
      return;
    }

    this.currentProductHandle = productHandle;
    this.currentTemplateView = templateView || "";
    this.showLoading();
    this.modal.style.display = "flex";
    document.body.style.overflow = "hidden";

    this.fetchProduct(productHandle);
  }

  close() {
    if (this.modal) this.modal.style.display = "none";
    document.body.style.overflow = "";
    this.currentProductHandle = null;
    this.currentProductData = null;
  }

  showLoading() {
    const t = window.quickAddTranslations || {};
    this.modalContent.innerHTML = `
      <div class="quick-add-loading">
        <p>${t.loading || "Loading product..."}</p>
      </div>
    `;
  }

  showError() {
    const t = window.quickAddTranslations || {};
    this.modalContent.innerHTML = `
      <div class="quick-add-error">
        <p>${t.error || "Failed to load product. Please try again."}</p>
        <button onclick="quickAddModal.close()">${t.close || "Close"}</button>
      </div>
    `;
  }

  async fetchProduct(productHandle) {
    try {
      const locale =
        document.documentElement.lang ||
        (window.Shopify &&
          window.Shopify.locale &&
          window.Shopify.locale.iso_code) ||
        "";
      const view = this.currentTemplateView || "";

      const params = new URLSearchParams();
      params.set("section_id", "product-quick-add");
      if (view) params.set("view", view);

      const localePrefix = locale === 'ar' ? `/${locale}` : "";
      const url = `${localePrefix}/products/${productHandle}?${params.toString()}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch product");

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const productHTML = doc.querySelector(".quick-add-product");

      if (!productHTML) throw new Error("Invalid product content");

      this.modalContent.innerHTML = "";
      this.modalContent.appendChild(productHTML);

      // Store product data
      this.currentProductData = JSON.parse(productHTML.dataset.productVariants);

      this.initProductScripts();
    } catch (error) {
      console.error("Quick add error:", error);
      this.showError();
    }
  }

  initProductScripts() {
    const productContainer =
      this.modalContent.querySelector(".quick-add-product");
    if (!productContainer) return;

    const productHandle = productContainer.dataset.productHandle;
    if (!productHandle) return;

    this.initImageSlider(productContainer);
    this.initImageZoom(productContainer);
    this.initVariantSelection(productContainer, productHandle);
    this.initQuantitySelector(productContainer);
    this.initFormSubmission(productContainer);
    this.initBuyNow(productContainer);
    this.initSizeChart(productContainer);
    this.initCustomize(productContainer);
  }

  async getQuickAddProperties(container) {
    const properties = {};
    const descEl = container.querySelector(
      "#quick-add-customize-description, .product-customize-textarea"
    );
    const description = descEl ? descEl.value.trim() : "";
    if (description) properties["description"] = description;

    const nameEl = container.querySelector("#quick-add-customize-name");
    if (nameEl) {
      const name = nameEl.value.trim();
      if (name) properties["Customize Name"] = name;
    }

    const imageInputs = container.querySelectorAll(".custom-image-input");
    if (imageInputs.length > 0) {
      const imagePromises = [];
      imageInputs.forEach((input) => {
        const file = input.files && input.files[0];
        if (!file) return;
        const imageNumber =
          input.dataset.imageNumber || input.id.replace(/\D/g, "") || "1";
        const promise = new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            properties[`Custom Image ${imageNumber}`] = file.name;
            properties[`Custom Image ${imageNumber} Data`] = e.target.result;
            resolve();
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        });
        imagePromises.push(promise);
      });
      await Promise.all(imagePromises);
    }
    return properties;
  }

  getSelectedOptions(container) {
    const selectedOptions = [];
    container.querySelectorAll(".quick-add-option").forEach((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      selectedOptions.push(checked ? checked.value : null);
    });
    return selectedOptions;
  }

  findVariantByOptions(container, selectedOptions) {
    if (!this.currentProductData) return null;
    const norm = (v) => (v == null ? "" : String(v).trim());
    return this.currentProductData.find((variant) =>
      variant.options.every((opt, idx) => norm(opt) === norm(selectedOptions[idx]))
    );
  }

  switchCustomizationVariant(container, targetValue) {
    const customizationInputs = container.querySelectorAll(
      'input[data-option-name="Customization"]'
    );
    if (customizationInputs.length === 0) return;
    const targetInput = Array.from(customizationInputs).find(
      (input) => (input.dataset.optionValue || input.value) === targetValue
    );
    if (!targetInput) return;
    targetInput.checked = true;
    const optionGroup = targetInput.closest(".quick-add-option");
    const optionsContainer = optionGroup?.querySelector(".quick-add-swatch") || optionGroup;
    if (optionsContainer) {
      optionsContainer.querySelectorAll(".option-value").forEach((l) => l.classList.remove("selected"));
      const label = optionsContainer.querySelector(`label[for="${targetInput.id}"]`);
      if (label) label.classList.add("selected");
    }
    const legendValue = container.querySelector(".customization-variant-hidden .selected-value, .quick-add-option.customization-variant-hidden [data-selected-value]");
    if (legendValue) legendValue.textContent = targetValue;
    const selectedOptions = this.getSelectedOptions(container);
    const variant = this.findVariantByOptions(container, selectedOptions);
    if (variant) {
      const idInput = container.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;
      const firstVal = customizationInputs[0]?.dataset?.optionValue;
      const isCustom = firstVal !== undefined && targetValue !== firstVal;
      this.updateQuickAddPriceDisplay(container, variant, isCustom);
      const buyNowBtn = container.querySelector(
        ".quick-add-buy-now, .buy-now-btn"
      );
      const buyNowWarn = container.querySelector(".buy-now-warn");
      if (buyNowBtn && buyNowWarn) {
        if (variant.available) {
          buyNowBtn.classList.remove("hidden");
          buyNowBtn.style.display = "";
          buyNowWarn.style.display = "none";
        } else {
          buyNowBtn.classList.add("hidden");
          buyNowBtn.style.display = "none";
          buyNowWarn.style.display = "block";
        }
      }
      const addToCartBtn = container.querySelector(".quick-add-atc");
      if (addToCartBtn) addToCartBtn.disabled = !variant.available;
    }
  }

  updateQuickAddPriceDisplay(container, variant, isCustom) {
    const currentPrice = container.querySelector(
      ".quick-add-current-price, .current-price"
    );
    const comparePrice = container.querySelector(
      ".quick-add-compare-price, .compare-price"
    );
    const savings = container.querySelector(".quick-add-savings, .savings");
    const customMsg = container.querySelector(".custom-price-message");
    if (isCustom) {
      if (currentPrice) currentPrice.style.display = "none";
      if (comparePrice) comparePrice.style.display = "none";
      if (savings) savings.style.display = "none";
      if (customMsg) customMsg.style.display = "block";
    } else {
      if (customMsg) customMsg.style.display = "none";
      if (currentPrice) {
        currentPrice.style.display = "";
        currentPrice.textContent = window.Shopify?.formatMoney
          ? Shopify.formatMoney(variant.price, window.money_format)
          : variant.price;
      }
      if (comparePrice && variant.compare_at_price > variant.price) {
        comparePrice.style.display = "";
        comparePrice.textContent = window.Shopify?.formatMoney
          ? Shopify.formatMoney(variant.compare_at_price, window.money_format)
          : variant.compare_at_price;
      } else if (comparePrice) comparePrice.style.display = "none";
      if (savings) {
        if (variant.compare_at_price > variant.price) {
          savings.style.display = "";
          const pct = Math.round(
            100 - (variant.price / variant.compare_at_price) * 100
          );
          savings.textContent = pct + "% SAVED";
        } else savings.style.display = "none";
      }
    }
  }

  initCustomize(container) {
    const toggle = container.querySelector(".product-customize-toggle");
    const content = container.querySelector("#quick-add-customize-content");
    const textarea = container.querySelector(
      "#quick-add-customize-description, .product-customize-textarea"
    );
    if (!toggle || !content) return;

    const customizationInputs = container.querySelectorAll(
      'input[data-option-name="Customization"]'
    );
    const firstCustomValue = customizationInputs[0]?.dataset?.optionValue;
    const secondCustomValue = customizationInputs[1]?.dataset?.optionValue;

    toggle.addEventListener("click", () => {
      const isExpanded = toggle.getAttribute("aria-expanded") === "true";
      const icon = toggle.querySelector(".customize-toggle-icon");
      if (isExpanded) {
        if (textarea && textarea.value.trim().length > 0) return;
        content.style.display = "none";
        toggle.setAttribute("aria-expanded", "false");
        if (icon) icon.textContent = "+";
      } else {
        content.style.display = "block";
        toggle.setAttribute("aria-expanded", "true");
        if (icon) icon.textContent = "−";
      }
    });

    if (textarea) {
      const showCustomPriceMessage = (show) => {
        const currentPrice = container.querySelector(
          ".quick-add-current-price, .current-price"
        );
        const comparePrice = container.querySelector(
          ".quick-add-compare-price, .compare-price"
        );
        const savings = container.querySelector(".quick-add-savings, .savings");
        const customMsg = container.querySelector(".custom-price-message");
        if (show) {
          if (currentPrice) currentPrice.style.display = "none";
          if (comparePrice) comparePrice.style.display = "none";
          if (savings) savings.style.display = "none";
          if (customMsg) customMsg.style.display = "block";
        } else {
          if (customMsg) customMsg.style.display = "none";
          if (currentPrice) currentPrice.style.display = "";
          if (comparePrice) comparePrice.style.display = "";
          if (savings) savings.style.display = "";
          const idInput = container.querySelector('input[name="id"]');
          const variantId = idInput ? idInput.value : null;
          const variant =
            this.currentProductData && variantId
              ? this.currentProductData.find(
                  (v) => String(v.id) === String(variantId)
                )
              : null;
          if (variant)
            this.updateQuickAddPriceDisplay(container, variant, false);
        }
      };

      textarea.addEventListener("input", () => {
        const hasText = textarea.value.trim().length > 0;
        if (hasText) {
          if (customizationInputs.length >= 2) {
            const targetValue = secondCustomValue;
            this.switchCustomizationVariant(container, targetValue);
          } else {
            showCustomPriceMessage(true);
          }
          textarea.classList.add("active");
          toggle.classList.add("active");
        } else {
          if (customizationInputs.length >= 2) {
            this.switchCustomizationVariant(container, firstCustomValue);
          } else {
            showCustomPriceMessage(false);
          }
          textarea.classList.remove("active");
          toggle.classList.remove("active");
        }
      });
      textarea.addEventListener("change", () => {
        const hasText = textarea.value.trim().length > 0;
        if (hasText) {
          if (customizationInputs.length >= 2)
            this.switchCustomizationVariant(container, secondCustomValue);
          else showCustomPriceMessage(true);
        } else {
          if (customizationInputs.length >= 2)
            this.switchCustomizationVariant(container, firstCustomValue);
          else showCustomPriceMessage(false);
        }
      });
    }

    const imageInputs = container.querySelectorAll(".custom-image-input");
    imageInputs.forEach((input) => {
      const box = input.closest(".custom-image-upload-box");
      const preview = box && box.querySelector(".custom-image-preview");
      const previewImg =
        preview && preview.querySelector(".custom-image-preview-img");
      const removeBtn = box && box.querySelector(".custom-image-remove");
      const imageNumber = input.dataset.imageNumber || "1";

      input.addEventListener("change", (e) => {
        const file = e.target.files[0];
        if (!file || !file.type.startsWith("image/")) {
          input.value = "";
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          input.value = "";
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          if (previewImg) previewImg.src = ev.target.result;
          if (preview) preview.style.display = "block";
          if (box) box.classList.add("has-preview");
        };
        reader.readAsDataURL(file);
      });

      if (removeBtn && box && preview) {
        removeBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          input.value = "";
          preview.style.display = "none";
          box.classList.remove("has-preview");
          if (previewImg) previewImg.src = "";
        });
      }
    });
  }

  initImageSlider(container) {
    const slider = container.querySelector(".quick-add-slides");
    const slides = container.querySelectorAll(".quick-add-slide");
    const prevBtn = container.querySelector(".quick-add-prev");
    const nextBtn = container.querySelector(".quick-add-next");
    const bullets = container.querySelectorAll(".quick-add-bullet");

    if (!slider || slides.length === 0) return;

    let currentSlide = 0;
    const slideCount = slides.length;

    const updateBullets = () => {
      bullets.forEach((bullet, index) => {
        bullet.classList.toggle("active", index === currentSlide);
        bullet.setAttribute("aria-selected", index === currentSlide);
      });
    };

    const goToSlide = (index) => {
      currentSlide = (index + slideCount) % slideCount;
      slider.style.transform = `translateX(-${currentSlide * 100}%)`;
      updateBullets();
    };

    const nextSlide = () => goToSlide(currentSlide + 1);
    const prevSlide = () => goToSlide(currentSlide - 1);

    if (nextBtn) nextBtn.addEventListener("click", nextSlide);
    if (prevBtn) prevBtn.addEventListener("click", prevSlide);

    bullets.forEach((bullet, index) => {
      bullet.addEventListener("click", () => goToSlide(index));
    });

    // Touch events for swipe
    let touchStartX = 0;
    let touchEndX = 0;

    slider.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    slider.addEventListener(
      "touchend",
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        if (touchEndX < touchStartX - 50) nextSlide();
        if (touchEndX > touchStartX + 50) prevSlide();
      },
      { passive: true }
    );

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    });

    updateBullets();
  }

  // Add these new methods for zoom functionality
  initImageZoom(container) {
    const zoomPopup = container.querySelector(".quick-image-zoom-popup");
    const zoomImages = container.querySelectorAll(".quick-zoom-image");
    const zoomImageContainer = container.querySelector(
      ".quick-zoom-image-container"
    );
    const zoomCloseBtn = container.querySelector(".quick-zoom-close-btn");
    const zoomPrevBtn = container.querySelector(".quick-zoom-nav.prev");
    const zoomNextBtn = container.querySelector(".quick-zoom-nav.next");
    const zoomCurrentSlide = container.querySelector(
      ".quick-zoom-counter .quick-current-slide"
    );
    const zoomTotalSlides = container.querySelector(
      ".quick-zoom-counter .quick-total-slides"
    );
    const mainZoomBtn = container.querySelector(".quick-main_zoom");

    if (!zoomPopup) return;

    // Initialize zoom popup
    const initZoomPopup = () => {
      zoomTotalSlides.textContent = zoomImages.length;

      // Open zoom popup
      mainZoomBtn.addEventListener("click", () => {
        const currentSlide = container.querySelector(
          ".quick-add-slide[data-index]"
        );
        this.currentZoomIndex = parseInt(currentSlide.dataset.index || "0");
        updateZoomPopup();
        zoomPopup.classList.add("active");
        document.body.style.overflow = "hidden";
      });

      // Close zoom popup
      zoomCloseBtn.addEventListener("click", () => {
        zoomPopup.classList.remove("active");
        document.body.style.overflow = "hidden"; // Keep hidden because modal is still open
      });

      // Close when clicking outside image
      zoomPopup.addEventListener("click", (e) => {
        if (e.target === zoomPopup) {
          zoomPopup.classList.remove("active");
          document.body.style.overflow = "hidden";
        }
      });

      // Keyboard navigation
      document.addEventListener("keydown", (e) => {
        if (zoomPopup.classList.contains("active")) {
          if (e.key === "Escape") {
            zoomPopup.classList.remove("active");
            document.body.style.overflow = "hidden";
          }
          if (e.key === "ArrowRight") nextZoomImage();
          if (e.key === "ArrowLeft") prevZoomImage();
        }
      });

      // Navigation buttons
      zoomPrevBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        prevZoomImage();
      });

      zoomNextBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        nextZoomImage();
      });

      // Swipe gestures for mobile
      let touchStartX = 0;
      let touchEndX = 0;

      zoomImageContainer.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      zoomImageContainer.addEventListener(
        "touchend",
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          handleZoomSwipe();
        },
        { passive: true }
      );

      const handleZoomSwipe = () => {
        if (touchEndX < touchStartX - 50) nextZoomImage();
        if (touchEndX > touchStartX + 50) prevZoomImage();
      };

      const nextZoomImage = () => {
        this.currentZoomIndex = (this.currentZoomIndex + 1) % zoomImages.length;
        updateZoomPopup();
      };

      const prevZoomImage = () => {
        this.currentZoomIndex =
          (this.currentZoomIndex - 1 + zoomImages.length) % zoomImages.length;
        updateZoomPopup();
      };

      const updateZoomPopup = () => {
        zoomImages.forEach((img, index) => {
          img.classList.toggle("active", index === this.currentZoomIndex);
        });
        if (zoomCurrentSlide) {
          zoomCurrentSlide.textContent = this.currentZoomIndex + 1;
        }
      };
    };

    initZoomPopup.call(this); // Call with this context
  }

  initVariantSelection(container, productHandle) {
    const variantRadios = container.querySelectorAll(
      '.quick-add-swatch input[type="radio"]'
    );
    const addToCartBtn = container.querySelector(".quick-add-atc");
    const priceDisplay = container.querySelector(".quick-add-current-price");
    const comparePriceDisplay = container.querySelector(
      ".quick-add-compare-price"
    );
    const savingsDisplay = container.querySelector(".quick-add-savings");

    if (variantRadios.length === 0) return;

    // Ensure clicking the label always selects the option (fixes unclickable labels)
    container.addEventListener("click", (e) => {
      const label = e.target.closest(".option-value.swatch-input__label");
      if (!label || !label.htmlFor) return;
      const radio = document.getElementById(label.htmlFor);
      if (!radio || radio.type !== "radio" || !container.contains(radio)) return;
      if (radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
      e.preventDefault();
    }, true);

    variantRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        const selectedOptions = [];

        container
          .querySelectorAll(".quick-add-option")
          .forEach((optionGroup) => {
            const selectedOption = optionGroup.querySelector(
              'input[type="radio"]:checked'
            );
            if (selectedOption) selectedOptions.push(selectedOption.value);
            else selectedOptions.push(null);
          });

        const selectedVariant = this.findVariantByOptions(container, selectedOptions);

        if (selectedVariant) {
          const variantInput = container.querySelector('input[name="id"]');
          if (variantInput) variantInput.value = selectedVariant.id;

          // Sync checked state and .selected class for each option group
          const optionGroups = container.querySelectorAll(".quick-add-option");
          selectedOptions.forEach((selectedValue, index) => {
            const group = optionGroups[index];
            if (!group || selectedValue == null) return;
            const optionsContainer =
              group.querySelector(".quick-add-swatch") || group;
            optionsContainer
              .querySelectorAll('input[type="radio"]')
              .forEach((radio) => {
                const label = optionsContainer.querySelector(
                  'label[for="' + radio.id + '"]'
                );
                if (radio.value === selectedValue) {
                  radio.checked = true;
                  if (label) label.classList.add("selected");
                } else {
                  radio.checked = false;
                  if (label) label.classList.remove("selected");
                }
              });
          });

          // Update price display
          if (priceDisplay) {
            priceDisplay.textContent = Shopify.formatMoney(
              selectedVariant.price,
              window.money_format
            );
          }

          if (comparePriceDisplay) {
            if (selectedVariant.compare_at_price > selectedVariant.price) {
              comparePriceDisplay.textContent = Shopify.formatMoney(
                selectedVariant.compare_at_price,
                window.money_format
              );
              comparePriceDisplay.style.display = "inline";
              if (savingsDisplay) {
                const saveValue = Math.round(
                  100 -
                    (selectedVariant.price / selectedVariant.compare_at_price) *
                      100
                );
                savingsDisplay.textContent = `${saveValue}% SAVED`;
                savingsDisplay.style.display = "inline";
              }
            } else {
              comparePriceDisplay.style.display = "none";
              if (savingsDisplay) savingsDisplay.style.display = "none";
            }
          }

          // Update add to cart button
          if (addToCartBtn) {
            addToCartBtn.disabled = !selectedVariant.available;
            addToCartBtn.textContent = selectedVariant.available
              ? addToCartBtn.dataset.addText || "Add to Cart"
              : addToCartBtn.dataset.soldOutText || "Sold Out";
          }

          // Update buy now button and warning (same as product page)
          const buyNowBtn = container.querySelector(
            ".quick-add-buy-now, .buy-now-btn"
          );
          const buyNowWarn = container.querySelector(".buy-now-warn");
          const quantitySelector =
            container.querySelector(".quantity-selector");
          if (buyNowBtn && buyNowWarn) {
            if (selectedVariant.available) {
              buyNowBtn.classList.remove("hidden");
              buyNowBtn.style.display = "";
              buyNowWarn.style.display = "none";
            } else {
              buyNowBtn.classList.add("hidden");
              buyNowBtn.style.display = "none";
              buyNowWarn.style.display = "block";
            }
          }
          if (quantitySelector) {
            quantitySelector.style.display = selectedVariant.available
              ? ""
              : "none";
          }

          // Update selected value labels
          const selectedValueEls = container.querySelectorAll(
            ".selected-value[data-selected-value]"
          );
          if (selectedValueEls.length && selectedVariant.options) {
            selectedVariant.options.forEach((opt, idx) => {
              if (selectedValueEls[idx])
                selectedValueEls[idx].textContent = opt;
            });
          }

          if (selectedVariant.featured_image) {
            const newImageSrc =
              selectedVariant.featured_image.src.split("?")[0] + "?width=800";
            const mainImage = container.querySelector(".quick-main-image");

            if (mainImage) {
              mainImage.src = newImageSrc;
              mainImage.alt = selectedVariant.featured_image.alt;
              mainImage.dataset.variantImage = selectedVariant.id;
            }
          }
        } else {
          // No matching variant (e.g. Single + Mix when Mix only exists for Couple): update legends, disable actions
          const selectedValueEls = container.querySelectorAll(".selected-value, [data-selected-value]");
          if (selectedOptions && selectedOptions.length && selectedValueEls.length) {
            selectedOptions.forEach((opt, idx) => {
              if (selectedValueEls[idx] && opt) selectedValueEls[idx].textContent = opt;
            });
          }
          const optionGroups = container.querySelectorAll(".quick-add-option");
          optionGroups.forEach((group) => {
            const optionsContainer = group.querySelector(".quick-add-swatch") || group;
            if (optionsContainer) {
              optionsContainer.querySelectorAll('input[type="radio"]').forEach((radio) => {
                const label = optionsContainer.querySelector('label[for="' + radio.id + '"]');
                if (label) label.classList.toggle("selected", radio.checked);
              });
            }
          });
          const variantInput = container.querySelector('input[name="id"]');
          if (variantInput) variantInput.value = "";
          if (addToCartBtn) {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent = addToCartBtn.dataset.soldOutText || "Sold Out";
          }
          const buyNowBtn = container.querySelector(".quick-add-buy-now, .buy-now-btn");
          const buyNowWarn = container.querySelector(".buy-now-warn");
          const quantitySelector = container.querySelector(".quantity-selector");
          if (buyNowBtn && buyNowWarn) {
            buyNowBtn.classList.add("hidden");
            buyNowBtn.style.display = "none";
            buyNowWarn.style.display = "block";
          }
          if (quantitySelector) quantitySelector.style.display = "none";
        }
      });
    });
  }

  initQuantitySelector(container) {
    const minusBtn = container.querySelector(".quantity-btn.minus");
    const plusBtn = container.querySelector(".quantity-btn.plus");
    const quantityInput = container.querySelector(".quantity-input");
    const stockError = container.querySelector(".stock-error");
    const productContainer = container.closest(".quick-add-product");

    if (!minusBtn || !plusBtn || !quantityInput) return;

    const getMaxQuantity = () => {
      const inventoryPolicy = productContainer.dataset.inventoryPolicy;
      const inventoryManagement = productContainer.dataset.inventoryManagement;

      if (inventoryManagement === "shopify" && inventoryPolicy === "deny") {
        return parseInt(productContainer.dataset.maxQuantity) || 9999;
      }
      return 9999; // No limit if not tracking inventory
    };

    minusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      if (currentValue > 1) {
        quantityInput.value = currentValue - 1;
        stockError.textContent = ""; // Clear error when decreasing
      }
    });

    plusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const maxQuantity = getMaxQuantity();

      if (currentValue >= maxQuantity) {
        const t = window.quickAddTranslations || {};
        stockError.textContent = (
          t.only_x_available || "Only {quantity} available in stock"
        ).replace("{quantity}", maxQuantity);
        return;
      }

      quantityInput.value = currentValue + 1;
      stockError.textContent = ""; // Clear error when increasing within limits
    });

    quantityInput.addEventListener("change", () => {
      const t = window.quickAddTranslations || {};
      const onlyX = t.only_x_available || "Only {quantity} available in stock";
      const maxQuantity = getMaxQuantity();
      let newValue = parseInt(quantityInput.value) || 1;

      if (newValue < 1) newValue = 1;
      if (newValue > maxQuantity) {
        newValue = maxQuantity;
        stockError.textContent = onlyX.replace("{quantity}", maxQuantity);
      } else {
        stockError.textContent = "";
      }

      quantityInput.value = newValue;
    });
  }

  initFormSubmission(container) {
    const form = container.querySelector(".quick-add-form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const submitBtn = form.querySelector(".quick-add-atc");
      const originalText = submitBtn.textContent;
      const quantityInput = form.querySelector(".quantity-input");
      const stockError = form.querySelector(".stock-error");
      const productContainer = container.closest(".quick-add-product");

      const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
      const maxQuantity =
        parseInt(productContainer.dataset.maxQuantity) || 9999;
      const inventoryPolicy = productContainer.dataset.inventoryPolicy;
      const inventoryManagement = productContainer.dataset.inventoryManagement;

      // Validate stock
      if (
        inventoryManagement === "shopify" &&
        inventoryPolicy === "deny" &&
        quantity > maxQuantity
      ) {
        stockError.textContent = `Only ${maxQuantity} available in stock`;
        quantityInput.value = maxQuantity;
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = submitBtn.dataset.addingText || "Adding...";

      try {
        const formData = new FormData(form);
        const variantId = formData.get("id");
        const properties = await this.getQuickAddProperties(container);
        const item = {
          id: variantId,
          quantity: Math.min(quantity, maxQuantity),
        };
        if (Object.keys(properties).length > 0) item.properties = properties;

        const cartUrl = window.routes.cart_add_url + ".js";

        const response = await fetch(cartUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [item],
          }),
        });

        const data = await response.json();

        if (response.ok) {
          submitBtn.textContent = "Added!";

          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            action: "add",
            variant: data,
            formData: Object.fromEntries(formData),
            product: this.currentProductData,
          });

          // Close modal after delay
          setTimeout(() => {
            this.close();
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 1000);
        } else {
          throw new Error(data.description || "Failed to add to cart");
        }
      } catch (error) {
        console.error("Error adding to cart:", error);

        // Publish error event
        publish(PUB_SUB_EVENTS.cartError, {
          error: error.message,
          context: "quick-add-modal",
          productHandle: this.currentProductHandle,
        });

        submitBtn.textContent = "Error - Try Again";
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      }
    });
  }

  initBuyNow(container) {
    const buyNowBtn = container.querySelector(
      ".quick-add-buy-now, .buy-now-btn"
    );
    const form = container.querySelector(".quick-add-form");
    if (!buyNowBtn || !form) return;

    const addingText = buyNowBtn.dataset.addingText || "Adding...";
    const originalBuyNowText = buyNowBtn.textContent;

    buyNowBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      const variantInput = form.querySelector('input[name="id"]');
      const quantityInput = form.querySelector(".quantity-input");
      const productContainer = container.closest(".quick-add-product");
      const quantity = quantityInput
        ? parseInt(quantityInput.value, 10) || 1
        : 1;
      const maxQuantity =
        parseInt(productContainer.dataset.maxQuantity, 10) || 9999;
      const variantId = variantInput ? variantInput.value : null;
      if (!variantId) return;

      buyNowBtn.disabled = true;
      buyNowBtn.textContent = addingText;
      try {
        const properties = await this.getQuickAddProperties(container);
        const item = {
          id: variantId,
          quantity: Math.min(quantity, maxQuantity),
        };
        if (Object.keys(properties).length > 0) item.properties = properties;

        const cartUrl =
          (window.routes && window.routes.cart_add_url
            ? window.routes.cart_add_url
            : "/cart/add") + ".js";
        const response = await fetch(cartUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [item],
          }),
        });
        const data = await response.json();
        if (response.ok) {
          window.location.href = "/checkout";
          return;
        }
        throw new Error(data.description || "Failed to add to cart");
      } catch (err) {
        console.error("Quick add buy now:", err);
        buyNowBtn.textContent = originalBuyNowText;
        buyNowBtn.disabled = false;
      }
    });
  }

  initSizeChart(container) {
    const sizeBtn = container.querySelector("[data-open-table]");
    const sizePopup = container.querySelector(".size-chart-popup");
    if (!sizePopup) return;
    const sizePopupContent = sizePopup.querySelector(".size-chart-content");
    const sizeClosePopup = sizePopup.querySelector(".size-chart-close");
    let isOpen = false;
    const toggleSizeChart = () => {
      isOpen = !isOpen;
      if (isOpen) {
        document.body.style.overflow = "hidden";
        sizePopup.classList.add("open");
      } else {
        document.body.style.overflow = "";
        sizePopup.classList.remove("open");
      }
    };
    if (sizeBtn) sizeBtn.addEventListener("click", toggleSizeChart);
    sizePopup.addEventListener("click", (e) => {
      if (isOpen && !sizePopupContent.contains(e.target)) toggleSizeChart();
    });
    if (sizeClosePopup)
      sizeClosePopup.addEventListener("click", toggleSizeChart);
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && isOpen) toggleSizeChart();
    });
  }
}

// Initialize everything when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Initialize global instances
  window.quickAddModal = new QuickAddModal();

  // Event delegation for color swatches
  document.addEventListener("click", function (e) {
    // Color swatches
    const colorOption = e.target.closest(
      ".card-product__color-option:not(.disabled)"
    );
    if (colorOption) {
      e.preventDefault();
      if (colorOption.getAttribute("aria-current") === "true") return;

      const productCard = colorOption.closest(".card-product");
      const desktopImage = productCard.querySelector(
        ".card-product__image-link.hidden-mobile .card-product__image"
      );
      const mobileImage = productCard.querySelector(
        ".card-product__image-container.visible-mobile .card-product__image"
      );

      // Update active state
      colorOption
        .closest(".card-product__colors")
        .querySelectorAll(".card-product__color-option")
        .forEach((btn) => btn.setAttribute("aria-current", "false"));
      colorOption.setAttribute("aria-current", "true");

      // Update images
      const newImageSrc = colorOption.dataset.imageSrc;
      const newMobileImageSrc = colorOption.dataset.imageSrcMobile;
      const newAltText = colorOption.dataset.imageAlt;

      if (desktopImage && newImageSrc) desktopImage.src = newImageSrc;
      if (mobileImage && newMobileImageSrc) mobileImage.src = newMobileImageSrc;
      if (desktopImage) desktopImage.alt = newAltText;
      if (mobileImage) mobileImage.alt = newAltText;
    }

    // Quick add buttons (works for both static and dynamic cards)
    const quickAddBtn = e.target.closest(".quick-add-btn");
    if (quickAddBtn && window.quickAddModal) {
      e.preventDefault();
      const productHandle = quickAddBtn.dataset.productHandle;
      const templateView = quickAddBtn.dataset.quickAddView || "";
      window.quickAddModal.open(productHandle, templateView);
    }
  });
});
